import { Stack, Typography } from '@mui/material';
import { DayContent, DayContentProps } from 'react-day-picker';
import { useIntl } from 'react-intl';

import { ROLE_VALUES } from '../../../../constants/customerConstant';
import { useAuth } from '../../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import {
  AvailabilityObject,
  IntervalsObject,
} from '../../../../interfaces/meeting';

interface CalendarCellProps extends DayContentProps {
  setAvailability: (
    date: string,
    isOpen: boolean,
    intervals: IntervalsObject[],
  ) => void;
  events: AvailabilityObject | undefined;
}

const styles = {
  dateBox: {
    height: '140px',
    border: '1px solid #D6D6D6',
    margin: '0px -1px -1px 0px',
    paddingTop: '5px',
    '&: hover': {
      backgroundColor: '#EEFFFD',
      borderColor: '#00CECF',
    },
  },
  bodyText: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 400,
    fontSize: { xs: '10px', lg: '12px', xl: '14px' },
    color: '#3D3D3D',
  },
  dateText: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '175%',
    width: '24px',
    height: '24px',
    textAlign: 'center',
    borderRadius: '50px',
    marginBottom: '10px',
  },
};

const CalendarCell = ({
  setAvailability,
  events,
  ...props
}: CalendarCellProps) => {
  const intl = useIntl();
  const { userType } = useAuth();
  const isAdmin =
    userType === ROLE_VALUES.TENANT_ADMIN ||
    userType === ROLE_VALUES.PARTNER_ADMIN;
  const { createToastNotification } = useToastNotificationContext();
  const isCurrDay = props.date.toDateString() == new Date().toDateString();
  const isFutureDay = props.date >= new Date();
  const isOutsideDate = props?.activeModifiers?.outside;

  const openAvailabilityHandler = () => {
    if (!isAdmin) return;
    if (!isOutsideDate && isFutureDay)
      setAvailability(props.date.toDateString(), true, events?.intervals ?? []);
    else if (!isFutureDay)
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Unable to edit schedule for current or past dates.',
        }),
        'error',
      );
  };
  return (
    <Stack
      justifyContent='flex-start'
      alignItems='center'
      sx={{
        ...styles.dateBox,
        cursor: isAdmin ? 'pointer' : 'default',
        backgroundColor: isOutsideDate ? '#E7E7E7' : 'transparent',
      }}
      onClick={openAvailabilityHandler}
    >
      <Typography
        sx={{
          ...styles.dateText,
          backgroundColor: isCurrDay ? '#009FA4' : 'transparent',
          color: isCurrDay ? '#FFFFFF' : '#181818',
        }}
      >
        <DayContent {...props} />
      </Typography>
      {!isOutsideDate &&
        events &&
        events.intervals &&
        events.intervals.slice(0, 3).map((interval) => (
          <Typography sx={styles.bodyText} key={interval.id}>
            {`${interval?.from} - ${interval?.to}`}
          </Typography>
        ))}
      {!isOutsideDate &&
        events &&
        events.intervals &&
        events.intervals.length > 3 && (
        <Typography sx={styles.bodyText}>
          {`+${events.intervals.length - 3} more`}
        </Typography>
      )}
    </Stack>
  );
};

export default CalendarCell;
